import { useState } from 'react'
import { Button, Card, Form, Input, message, Space, Typography } from 'antd'
import { SafetyOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import apiClient from '../../api/client'
import type { SessionUser } from '../../stores/auth'
import { useAuthStore } from '../../stores/auth'

interface LoginValues {
  email: string
  password: string
}

interface LoginResp {
  actor?: string
  role?: string
  permissions?: string[]
  expires_at?: string
  totp_required?: boolean
  challenge_token?: string
}

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setUser = useAuthStore((s) => s.setUser)
  const [challenge, setChallenge] = useState<string | null>(null)
  const [useRecovery, setUseRecovery] = useState(false)

  const onCredentialFinish = async (vals: LoginValues) => {
    try {
      const res = await apiClient.post<{ data: LoginResp }>(
        '/api/v1/auth/login', vals,
      )
      const body = res.data.data
      if (body.totp_required && body.challenge_token) {
        setChallenge(body.challenge_token)
        return
      }
      setUser(body as SessionUser)
      message.success(t('auth.loginTitle'))
      navigate('/')
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } }
      message.error(err.response?.data?.message || t('auth.loginFailed'))
    }
  }

  const onTOTPFinish = async (vals: { code?: string; recovery?: string }) => {
    if (!challenge) return
    try {
      const payload: Record<string, unknown> = { challenge_token: challenge }
      if (useRecovery) {
        payload.recovery_code = vals.recovery
      } else {
        payload.code = vals.code
      }
      const res = await apiClient.post<{ data: SessionUser }>(
        '/api/v1/auth/login/verify-totp', payload,
      )
      setUser(res.data.data)
      message.success(t('auth.loginTitle'))
      navigate('/')
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } }
      message.error(err.response?.data?.message || t('auth.totpFailed', '2FA-Code ungültig'))
    }
  }

  return (
    <div className="auth-shell">
      <Card className="auth-card max-w-420">
        <Typography.Title level={3} className="mt-0">
          {t('auth.loginTitle')}
        </Typography.Title>
        {!challenge && (
          <Form layout="vertical" onFinish={onCredentialFinish}>
            <Form.Item
              label={t('auth.email')}
              name="email"
              rules={[{ required: true, type: 'email' }]}
            >
              <Input autoComplete="email" autoFocus />
            </Form.Item>
            <Form.Item
              label={t('auth.password')}
              name="password"
              rules={[{ required: true }]}
            >
              <Input.Password autoComplete="current-password" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                {t('auth.login')}
              </Button>
            </Form.Item>
          </Form>
        )}
        {challenge && (
          <Form layout="vertical" onFinish={onTOTPFinish}>
            <Space direction="vertical" className="full-width-stack">
              <Typography.Paragraph>
                <SafetyOutlined />{' '}
                {useRecovery
                  ? t('auth.recoveryHint', 'Recovery-Code eingeben:')
                  : t('auth.totpHint', '6-stelligen Code aus deiner Authenticator-App eingeben:')}
              </Typography.Paragraph>
              {!useRecovery && (
                <Form.Item name="code" rules={[{ required: true, len: 6 }]}>
                  <Input placeholder="123456" maxLength={6} autoFocus />
                </Form.Item>
              )}
              {useRecovery && (
                <Form.Item name="recovery" rules={[{ required: true }]}>
                  <Input placeholder="abcd1234ef" autoFocus />
                </Form.Item>
              )}
              <Button type="primary" htmlType="submit" block>
                {t('auth.verifyTotp', 'Verifizieren')}
              </Button>
              <Button type="link" block onClick={() => setUseRecovery(!useRecovery)}>
                {useRecovery
                  ? t('auth.useTotp', 'TOTP-Code stattdessen')
                  : t('auth.useRecovery', 'Recovery-Code stattdessen')}
              </Button>
              <Button type="link" block onClick={() => setChallenge(null)}>
                {t('common.cancel', 'Abbrechen')}
              </Button>
            </Space>
          </Form>
        )}
      </Card>
    </div>
  )
}
